import range from 'lodash/range';
import React, { useState, useCallback } from 'react';
import {
  ArrayLayoutProps,
  composePaths,
  computeLabel,
  convertDateToString,
  hasType,
  JsonSchema,
  Resolve,
} from '@jsonforms/core';
import map from 'lodash/map';
import { ArrayLayoutToolbar } from './ArrayToolbar';
import ExpandPanelRenderer from './ExpandPanelRenderer';
import merge from 'lodash/merge';
import { Box } from '@mantine/core';
import { cloneDeep } from 'lodash';

export const extractDefaults = (schema: JsonSchema, rootSchema: JsonSchema): any => {
  if (hasType(schema, 'object') && schema.oneOf) {
    const property = schema.oneOf[0];
    const resolvedProperty = property.$ref ? Resolve.schema(rootSchema, property.$ref, rootSchema) : property;
    return extractDefaults(resolvedProperty, rootSchema);
  }
  if (hasType(schema, 'object') && schema.default === undefined) {
    const result: { [key: string]: any } = {};
    for (const key in schema.properties) {
      const property = schema.properties[key];
      const resolvedProperty = property.$ref ? Resolve.schema(rootSchema, property.$ref, rootSchema) : property;

      if (hasType(resolvedProperty, 'object')) {
        result[key] = extractDefaults(resolvedProperty, rootSchema);
      }
      if (resolvedProperty.default !== undefined) {
        result[key] = cloneDeep(resolvedProperty.default);
      }
    }
    return result;
  }
  return cloneDeep(schema.default);
};

export const createDefaultValue = (schema: JsonSchema, rootSchema: JsonSchema) => {
  const resolvedSchema = Resolve.schema(schema, schema.$ref!, rootSchema);
  if (resolvedSchema.default !== undefined) {
    return extractDefaults(resolvedSchema, rootSchema);
  }
  if (hasType(resolvedSchema, 'string')) {
    if (resolvedSchema.format === 'date-time' || resolvedSchema.format === 'date' || resolvedSchema.format === 'time') {
      return convertDateToString(new Date(), resolvedSchema.format);
    }
    return '';
  } else if (hasType(resolvedSchema, 'integer') || hasType(resolvedSchema, 'number')) {
    return 0;
  } else if (hasType(resolvedSchema, 'boolean')) {
    return false;
  } else if (hasType(resolvedSchema, 'array')) {
    return [];
  } else if (hasType(resolvedSchema, 'object')) {
    return extractDefaults(resolvedSchema, rootSchema);
  } else if (hasType(resolvedSchema, 'null')) {
    return null;
  } else {
    return {};
  }
};

const MantineArrayLayoutComponent = (
  props: ArrayLayoutProps & { disableAdd?: boolean; disableRemove?: boolean; maxItems?: number }
) => {
  const [expanded, setExpanded] = useState<string | boolean>(false);
  const innerCreateDefaultValue = useCallback(
    () => createDefaultValue(props.schema, props.rootSchema),
    [props.rootSchema, props.schema]
  );
  const handleChange = useCallback(
    (panel: string) => (_event: any, expandedPanel: boolean) => {
      setExpanded(expandedPanel ? panel : false);
    },
    []
  );
  const isExpanded = (index: number) => expanded === composePaths(props.path, `${index}`);

  const {
    enabled,
    data,
    path,
    schema,
    uischema,
    errors,
    addItem,
    renderers,
    cells,
    label,
    required,
    rootSchema,
    config,
    uischemas,
    translations,
    description,
    disableAdd,
    disableRemove,
    minItems,
    removeItems,
  } = props;
  const appliedUiSchemaOptions = merge({}, config, props.uischema.options);
  const doDisableAdd = disableAdd || appliedUiSchemaOptions.disableAdd || (props.maxItems && data >= props.maxItems);
  const doDisableRemove = disableRemove || appliedUiSchemaOptions.disableRemove || minItems === data;

  return (
    <Box>
      <ArrayLayoutToolbar
        translations={translations}
        label={label} //{computeLabel(label, !!required, appliedUiSchemaOptions.hideRequiredAsterisk)}
        description={description ?? ''}
        errors={errors}
        path={path}
        enabled={enabled}
        addItem={addItem}
        createDefault={innerCreateDefaultValue}
        disableAdd={doDisableAdd}
        itemsNumber={data}
        minItems={minItems}
        removeAllItems={required === true ? undefined : removeItems}
        schema={schema}
      />
      {data > 0 ? (
        map(range(data), (index) => {
          return (
            <ExpandPanelRenderer
              enabled={enabled}
              index={index}
              expanded={isExpanded(index)}
              schema={schema}
              path={path}
              handleExpansion={handleChange}
              uischema={uischema}
              renderers={renderers}
              cells={cells}
              key={index}
              rootSchema={rootSchema}
              enableMoveUp={index != 0}
              enableMoveDown={index < data - 1}
              config={config}
              childLabelProp={appliedUiSchemaOptions.elementLabelProp}
              uischemas={uischemas}
              translations={translations}
              disableRemove={doDisableRemove}
            />
          );
        })
      ) : (
        <p>{translations.noDataMessage}</p>
      )}
    </Box>
  );
};

export const MantineArrayLayout = React.memo(MantineArrayLayoutComponent);
